<script setup>
import {
  ref,
  computed,
  watch,
  nextTick,
  onMounted,
  onBeforeUnmount,
} from 'vue';
import wootConstants from 'dashboard/constants/globals';
import VueDatePicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';

const props = defineProps({
  initialState: {
    type: Object,
    default: () => ({
      actives: 'all',
      sort: wootConstants.POSTS_SORT_BY.NEWEST,
    }),
  },
});

const emit = defineEmits(['filter']);

const showMenu = ref({
  sort: false,
  actives: false,
});

const sortOptions = [
  {
    name: 'Mais novos',
    key: wootConstants.POSTS_SORT_BY.NEWEST,
    type: wootConstants.INBOX_FILTER_TYPE.SORT_ORDER,
  },
  {
    name: 'Mais antigos',
    key: wootConstants.POSTS_SORT_BY.OLDEST,
    type: wootConstants.INBOX_FILTER_TYPE.SORT_ORDER,
  },
  {
    name: 'Maior número de comentários',
    key: wootConstants.POSTS_SORT_BY.COMMENTS_COUNT_DESC,
    type: wootConstants.INBOX_FILTER_TYPE.SORT_ORDER,
  },
  {
    name: 'Menor número de comentários',
    key: wootConstants.POSTS_SORT_BY.COMMENTS_COUNT_ASC,
    type: wootConstants.INBOX_FILTER_TYPE.SORT_ORDER,
  },
];

const activesOptions = [
  {
    name: 'Todos',
    key: wootConstants.POSTS_FILTER_TYPES.ALL,
    type: wootConstants.INBOX_FILTER_TYPE.ACTIVES,
  },
  {
    name: 'Ativos',
    key: wootConstants.POSTS_FILTER_TYPES.ACTIVE,
    type: wootConstants.INBOX_FILTER_TYPE.ACTIVES,
  },
  {
    name: 'Inativos',
    key: wootConstants.POSTS_FILTER_TYPES.INACTIVE,
    type: wootConstants.INBOX_FILTER_TYPE.ACTIVES,
  },
];

const activeOption = ref({
  sort: wootConstants.POSTS_SORT_BY.NEWEST,
  actives: wootConstants.POSTS_FILTER_TYPES.ALL,
  publicationDateStart: null,
  publicationDateEnd: null,
});

const sortButtonRect = ref(null);
const activesButtonRect = ref(null);
const sortButtonRef = ref(null);
const activesButtonRef = ref(null);

const currentOption = computed(() => {
  return {
    sort:
      sortOptions.find(option => option.key === activeOption.value.sort)
        ?.name || '',
    actives:
      activesOptions.find(option => option.key === activeOption.value.actives)
        ?.name || '',
  };
});

const onMenuOpen = menu => {
  // Close other menus
  Object.keys(showMenu.value).forEach(key => {
    if (key !== menu) {
      showMenu.value[key] = false;
    }
  });
  showMenu.value[menu] = !showMenu.value[menu];

  if (showMenu.value[menu]) {
    nextTick(() => {
      if (menu === 'sort' && sortButtonRef.value) {
        sortButtonRect.value = sortButtonRef.value.getBoundingClientRect();
      } else if (menu === 'actives' && activesButtonRef.value) {
        activesButtonRect.value =
          activesButtonRef.value.getBoundingClientRect();
      }
    });
  }
};

const onMenuOptionClick = (option, type) => {
  activeOption.value[type] = option.key;
  showMenu.value[type] = false;
  emit('filter', activeOption.value);
};

const onDateChange = (value, field) => {
  activeOption.value[field] = value;
  emit('filter', activeOption.value);
};

const getDropdownStyle = buttonRect => {
  if (!buttonRect) return {};
  return {
    position: 'fixed',
    top: `${buttonRect.bottom + 4}px`,
    left: `${buttonRect.left}px`,
    width: `${buttonRect.width}px`,
  };
};

const handleClickOutside = event => {
  const sortDropdown = document.querySelector('[data-dropdown="sort"]');
  const activesDropdown = document.querySelector('[data-dropdown="actives"]');
  const datePickerElements = document.querySelectorAll(
    '.dp__menu, .dp__input_wrap, [class*="vue-date-picker"]'
  );

  const isDatePickerClick = Array.from(datePickerElements).some(el =>
    el?.contains(event.target)
  );

  if (isDatePickerClick) {
    return;
  }

  if (
    showMenu.value.sort &&
    sortButtonRef.value &&
    !sortButtonRef.value.contains(event.target) &&
    (!sortDropdown || !sortDropdown.contains(event.target))
  ) {
    showMenu.value.sort = false;
  }

  if (
    showMenu.value.actives &&
    activesButtonRef.value &&
    !activesButtonRef.value.contains(event.target) &&
    (!activesDropdown || !activesDropdown.contains(event.target))
  ) {
    showMenu.value.actives = false;
  }
};

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside);
});

watch(
  () => props.initialState,
  newState => {
    activeOption.value = {
      sort: newState.sort || wootConstants.POSTS_SORT_BY.NEWEST,
      actives: newState.actives || wootConstants.POSTS_FILTER_TYPES.ALL,
      publicationDateStart: newState.publicationDateStart || null,
      publicationDateEnd: newState.publicationDateEnd || null,
    };
  },
  { immediate: true }
);
</script>

<template>
  <div
    class="flex flex-col bg-white dark:bg-slate-800 w-full sm:w-[420px] md:w-[480px] border shadow-lg border-slate-200 dark:border-slate-700/50 rounded-xl overflow-visible"
  >
    <!-- Sort Section -->
    <div
      class="flex flex-col gap-2 p-4 border-b border-slate-100 dark:border-slate-700/50"
    >
      <label
        class="text-xs font-semibold text-slate-700 dark:text-slate-200 uppercase tracking-wide"
      >
        {{ $t('COMMENTS.FILTER.SORT_BY') }}
      </label>
      <div class="relative">
        <button
          ref="sortButtonRef"
          type="button"
          class="w-full h-10 flex items-center justify-between px-3 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg text-sm font-medium text-slate-700 dark:text-slate-300 hover:border-slate-300 dark:hover:border-slate-600 transition-colors focus:outline-none focus:ring-2 focus:ring-woot-500 focus:border-transparent"
          @click="onMenuOpen('sort')"
        >
          <span>{{ currentOption.sort }}</span>
          <fluent-icon
            icon="chevron-down"
            size="16"
            class="text-slate-500 dark:text-slate-400 transition-transform"
            :class="{ 'rotate-180': showMenu.sort }"
          />
        </button>
        <Teleport to="body">
          <div
            v-if="showMenu.sort"
            :style="getDropdownStyle(sortButtonRect)"
            data-dropdown="sort"
            class="bg-white dark:bg-slate-800 rounded-lg shadow-xl border border-slate-200 dark:border-slate-700 z-[9999] overflow-hidden"
          >
            <button
              v-for="option in sortOptions"
              :key="option.key"
              type="button"
              class="w-full flex items-center justify-between px-3 py-2.5 text-sm font-medium hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors"
              :class="{
                'bg-woot-50 dark:bg-woot-900/20 text-woot-600 dark:text-woot-400':
                  activeOption.sort === option.key,
                'text-slate-700 dark:text-slate-300':
                  activeOption.sort !== option.key,
              }"
              @click.stop="onMenuOptionClick(option, 'sort')"
            >
              <span>{{ option.name }}</span>
              <fluent-icon
                v-if="activeOption.sort === option.key"
                icon="checkmark"
                size="16"
                class="text-woot-600 dark:text-woot-400"
              />
            </button>
          </div>
        </Teleport>
      </div>
    </div>

    <div
      class="flex flex-col gap-2 p-4 border-b border-slate-100 dark:border-slate-700/50"
      @click.stop
    >
      <label
        class="text-xs font-semibold text-slate-700 dark:text-slate-200 uppercase tracking-wide"
      >
        {{ $t('COMMENTS.FILTER.PUBLICATION_DATE') }}
      </label>
      <div class="flex flex-col sm:flex-row gap-2 text-sm">
        <div class="flex items-center gap-2 flex-1">
          <span
            class="text-slate-600 dark:text-slate-400 whitespace-nowrap text-xs"
          >
            {{ $t('COMMENTS.FILTER.FROM') }}
          </span>
          <VueDatePicker
            :model-value="activeOption.publicationDateStart"
            class="flex-1 [&_.dp__input]:focus:outline-none [&_.dp__input]:focus:ring-2 [&_.dp__input]:focus:ring-woot-500"
            auto-apply
            date-picker
            :enable-time-picker="false"
            locale="pt-BR"
            format="dd/MM/yyyy"
            hide-input-icon
            @update:model-value="e => onDateChange(e, 'publicationDateStart')"
            @click.stop
          />
        </div>
        <div class="flex items-center gap-2 flex-1">
          <span
            class="text-slate-600 dark:text-slate-400 whitespace-nowrap text-xs"
          >
            {{ $t('COMMENTS.FILTER.TO') }}
          </span>
          <VueDatePicker
            :model-value="activeOption.publicationDateEnd"
            class="flex-1 [&_.dp__input]:focus:outline-none [&_.dp__input]:focus:ring-2 [&_.dp__input]:focus:ring-woot-500"
            auto-apply
            date-picker
            :enable-time-picker="false"
            locale="pt-BR"
            format="dd/MM/yyyy"
            hide-input-icon
            @update:model-value="e => onDateChange(e, 'publicationDateEnd')"
            @click.stop
          />
        </div>
      </div>
    </div>

    <!-- Actives Filter Section -->
    <div class="flex flex-col gap-2 p-4">
      <label
        class="text-xs font-semibold text-slate-700 dark:text-slate-200 uppercase tracking-wide"
      >
        {{ $t('COMMENTS.FILTER.FILTER_BY_STATUS') }}
      </label>
      <div class="relative">
        <button
          ref="activesButtonRef"
          type="button"
          class="w-full h-10 flex items-center justify-between px-3 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg text-sm font-medium text-slate-700 dark:text-slate-300 hover:border-slate-300 dark:hover:border-slate-600 transition-colors focus:outline-none focus:ring-2 focus:ring-woot-500 focus:border-transparent"
          @click="onMenuOpen('actives')"
        >
          <span>{{ currentOption.actives }}</span>
          <fluent-icon
            icon="chevron-down"
            size="16"
            class="text-slate-500 dark:text-slate-400 transition-transform"
            :class="{ 'rotate-180': showMenu.actives }"
          />
        </button>
        <Teleport to="body">
          <div
            v-if="showMenu.actives"
            :style="getDropdownStyle(activesButtonRect)"
            data-dropdown="actives"
            class="bg-white dark:bg-slate-800 rounded-lg shadow-xl border border-slate-200 dark:border-slate-700 z-[9999] overflow-hidden"
          >
            <button
              v-for="option in activesOptions"
              :key="option.key"
              type="button"
              class="w-full flex items-center justify-between px-3 py-2.5 text-sm font-medium hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors"
              :class="{
                'bg-woot-50 dark:bg-woot-900/20 text-woot-600 dark:text-woot-400':
                  activeOption.actives === option.key,
                'text-slate-700 dark:text-slate-300':
                  activeOption.actives !== option.key,
              }"
              @click.stop="onMenuOptionClick(option, 'actives')"
            >
              <span>{{ option.name }}</span>
              <fluent-icon
                v-if="activeOption.actives === option.key"
                icon="checkmark"
                size="16"
                class="text-woot-600 dark:text-woot-400"
              />
            </button>
          </div>
        </Teleport>
      </div>
    </div>
  </div>
</template>
